import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileArchive, FileSpreadsheet, Loader2, Plus, ShieldCheck } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { api, downloadFile, fmtDate } from "../../lib/api";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
const total = (counts) => Object.values(counts).reduce((a, b) => a + b, 0);

const ExportButton = ({ path, name, label, icon: Icon, testid }) => {
  const { t } = useLang();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadFile(path, name); } catch { toast.error(t.errors.generic); }
    setBusy(false);
  };
  return (
    <button type="button" className="btn btn-ghost" onClick={run} disabled={busy} data-testid={testid}>
      {busy ? <Loader2 className="spin" aria-hidden="true" /> : <Icon aria-hidden="true" />}{label}
    </button>
  );
};

const BackupRow = ({ b }) => {
  const { t, lang } = useLang();
  const x = t.backup;
  const time = new Date(b.created_at).toLocaleTimeString(lang === "en" ? "en-GB" : "fr-FR", { hour: "2-digit", minute: "2-digit" });
  return (
    <tr data-testid={`backup-row-${b.id}`}>
      <td className="mono">{fmtDate(b.created_at, lang)} {time}</td>
      <td>{x.kinds[b.kind] || b.kind}</td>
      <td className="mono">{fmtSize(b.size)}</td>
      <td style={{ fontSize: 12 }} title={Object.entries(b.counts).map(([k, v]) => `${k}: ${v}`).join("\n")}>
        {total(b.counts)} {x.docs} · {["clients", "experts", "diagnostics", "interventions"].map((k) => `${k} ${b.counts[k] ?? 0}`).join(" · ")}
      </td>
      <td>{x.where[b.storage] || b.storage}</td>
      <td>
        <ExportButton path={`/admin/backups/${b.id}/download`} name={`${b.name}`} label={x.download} icon={Download} testid={`backup-download-btn-${b.id}`} />
      </td>
    </tr>
  );
};

export const Backup = () => {
  const { t } = useLang();
  const x = t.backup;
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => setRows((await api.get("/admin/backups")).data), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try { await api.post("/admin/backups"); toast.success(x.created); await load(); } catch { toast.error(x.failed); }
    setBusy(false);
  };

  return (
    <div data-testid="admin-backup-panel">
      <h3 className="sub-title">{x.title}</h3>
      <p className="info-note" style={{ marginTop: 12 }}><ShieldCheck aria-hidden="true" /><span>{x.desc}</span></p>
      <div className="f-actions" style={{ justifyContent: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <ExportButton path="/admin/backup/export" name="indushub-export.zip" label={x.exportAll} icon={FileArchive} testid="backup-export-all-btn" />
        <ExportButton path="/admin/backup/csv/clients" name="indushub-clients.csv" label={x.csvClients} icon={FileSpreadsheet} testid="backup-export-clients-btn" />
        <ExportButton path="/admin/backup/csv/experts" name="indushub-experts.csv" label={x.csvExperts} icon={FileSpreadsheet} testid="backup-export-experts-btn" />
      </div>
      <div className="settings-block">
        <div className="sub-head">
          <h3 className="sub-title">{x.storedTitle}</h3>
          <button type="button" className="btn btn-primary" onClick={create} disabled={busy} data-testid="backup-create-btn">
            {busy ? <Loader2 className="spin" aria-hidden="true" /> : <Plus aria-hidden="true" />}{x.createNow}
          </button>
        </div>
        <p className="f-note" style={{ margin: "8px 0 14px" }}>{x.storedDesc}</p>
        {rows === null ? <Loader2 className="spin" aria-label={t.common.loading} /> : !rows.length
          ? <p className="empty" data-testid="backup-empty">{x.none}</p>
          : (
            <div className="table-wrap"><table className="data-table" data-testid="backup-table" style={{ minWidth: 860 }}>
              <thead><tr>{["date", "kind", "size", "content", "where", "dl"].map((k) => <th key={k}>{x.h[k]}</th>)}</tr></thead>
              <tbody>{rows.map((b) => <BackupRow key={b.id} b={b} />)}</tbody>
            </table></div>
          )}
      </div>
    </div>
  );
};
